<script setup lang="ts">
import type { SelectedRegionsSectionClick } from '@/components/Sections/SelectedRegionsSection';
import type { ViewMoreClickedPayload } from '~/components/Sections';
import type { PropertyTypeEnum } from '~/core/entities/CRMEnums';
import type { PaginatedPropertyFilters } from '~/core/entities/property/filters';
import type {
	CardHeroOpened,
	FilterApplied,
	QuickNeighborhoodFilterApplied,
	SectionName
} from '~/types/mixpanel';
import type { NuxtListPropertiesApiReturn } from '~~/server/api/properties/index.get';

import { regions } from '@@/mocks/static/regions';
import { testimonials } from '@@/mocks/static/testimonials';
import { defaultTwitterCardMeta } from '@/constants/seo';
import { useEvents } from '~/composables/events/useEvents';
import { useHomepageSeo } from '~/composables/seo/useHomepageSeo';
import { useAbsoluteUrl } from '~/composables/useAbsoluteUrl';
import { useFeatureFlags } from '~/composables/useFeatureFlags';
import { MixpanelProps } from '~/core/service/mixpanel';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { nuxtPropertiesRepository } from '~~/adapters/repositories/nuxt/nuxtPropertiesRepository';

definePageMeta({
	layout: 'default-transparent',
	path: '/'
});

const { ft_cwb_home_sliders } = useFeatureFlags();
const { hasPreferences, getPreferences } = usePreferencesCookie();

const defaultPropertiesReturn = {
	properties: [] as NuxtListPropertiesApiReturn['properties'],
	pagination: {
		totalItems: 0,
		totalPages: 0,
		currentPage: 1,
		itemsPerPage: 12,
		page: 1,
		perPage: 12,
		filteredCount: 0
	},
	locationSlugFilters: []
} as NuxtListPropertiesApiReturn;

const { toAbsoluteUrl } = useAbsoluteUrl();
const ogImageUrl = toAbsoluteUrl('/images/social/openGraph/og_home.webp');
const { title: homepageTitle, description: homepageDescription } =
	useHomepageSeo();

useSeoMeta({
	title: homepageTitle,
	description: homepageDescription,
	keywords: 'imóveis, alto padrão, PilarHomes, endereço',
	ogSiteName: 'PilarHomes',
	ogTitle: homepageTitle,
	ogDescription: homepageDescription,
	ogImage: ogImageUrl,
	twitterCard: defaultTwitterCardMeta,
	twitterTitle: homepageTitle,
	twitterDescription: homepageDescription,
	twitterImage: ogImageUrl
});
const { user } = useUserSession();

async function getProperties(
	filters: Partial<PaginatedPropertyFilters>
): Promise<NuxtListPropertiesApiReturn | null> {
	try {
		const getCookieData = useCookieData();
		const [result, error] = await nuxtPropertiesRepository.list(
			{
				...filters,
				markUserWishlistsId: user.value?.id
			},
			getCookieData()
		);

		if (error) {
			return defaultPropertiesReturn;
		}

		return result;
	} catch {
		return defaultPropertiesReturn;
	}
}

const { data: exclusiveProperties, status: isExclusiveRequestStatus } =
	useAsyncData<NuxtListPropertiesApiReturn>(
		'home:is-exclusive-properties',
		async () => {
			const properties = await getProperties({
				page: 1,
				perPage: 12,
				isExclusive: true,
				sortBy: 'portal_home_select'
			});

			if (!properties) {
				return defaultPropertiesReturn;
			}

			return properties;
		},
		{
			default: () => defaultPropertiesReturn,
			lazy: true
		}
	);

const isLoadingExclusiveProperties = computed(() => {
	return isExclusiveRequestStatus.value === 'pending';
});

const { data: selectedProperties, status: selectedPropertiesRequestStatus } =
	useAsyncData<NuxtListPropertiesApiReturn | null>(
		'home:selected-properties',
		async () => {
			const preferences = getPreferences();

			const maxAskingPrice = preferences?.askingPrice
				? Math.ceil(preferences?.askingPrice * 1.15)
				: undefined;

			const properties = await getProperties({
				page: 1,
				perPage: ft_cwb_home_sliders ? 15 : 5,
				isChosenForYou: ft_cwb_home_sliders ? undefined : true,
				regions: (preferences?.regions as string[]) || undefined,
				maxAskingPrice,
				sortBy: ft_cwb_home_sliders ? 'asking_price' : undefined,
				orderBy: ft_cwb_home_sliders ? 'desc' : undefined
			});

			if (!ft_cwb_home_sliders && (properties?.properties?.length ?? 0) < 5) {
				const getCookieData = useCookieData();
				const [chosenForYouProperties, error] =
					await nuxtPropertiesRepository.list(
						{
							page: 1,
							perPage: 5,
							isChosenForYou: true,
							markUserWishlistsId: user.value?.id
						},
						getCookieData()
					);

				if (!chosenForYouProperties || error) {
					return defaultPropertiesReturn;
				}

				return chosenForYouProperties;
			}

			if ((properties?.properties?.length ?? 0) < 5) {
				return null;
			}

			return properties;
		},
		{
			default: () => defaultPropertiesReturn,
			lazy: true,
			server: false
		}
	);

/**
 * Loading state for selected properties.
 * Includes 'idle' state to show skeleton immediately when preferences exist,
 * before the fetch even starts on the client.
 */
const isLoadingSelectedProperties = computed(() => {
	const status = selectedPropertiesRequestStatus.value;
	return status === 'idle' || status === 'pending';
});

const ctaLink = computed<string>(() => {
	const preferences = getPreferences();
	const maxAskingPrice = preferences?.askingPrice
		? Math.ceil(preferences?.askingPrice * 1.15)
		: undefined;

	const path = PropertyFiltersPresenter.getPathFromFilters({
		isChosenForYou: true,
		regions: (preferences?.regions as string[]) || undefined,
		states: (preferences?.states as string[]) || ['sp'],
		cities: (preferences?.cities as string[]) || undefined,
		propertyTypes: preferences?.propertyTypes as PropertyTypeEnum[],
		maxAskingPrice
	});

	return path;
});

/**
 * Shows the selected properties section (with skeleton while loading).
 * Only renders on client when user has preferences cookie.
 * Hides entirely if loading finished but has < 5 properties.
 */
const shouldShowSelectedProperties = computed(() => {
	// Must have feature flag and preferences
	if (!ft_cwb_home_sliders || !hasPreferences.value) return false;

	// While loading, show skeleton
	if (isLoadingSelectedProperties.value) return true;

	// After loading, only show if has enough properties
	return (selectedProperties.value?.properties?.length ?? 0) >= 5;
});

const testimonialsSectionVariant = computed(() => {
	return ft_cwb_home_sliders ? 'light' : 'dark';
});

const {
	trackCtaOffMarketClicked,
	trackCtaViewMoreClicked,
	trackCarouselNeighborhoodClicked,
	trackEmptyStateFilter,
	trackFilterApplied,
	trackQuickNeighborhoodFilterApplied,
	trackCardHeroOpened
} = useEvents();

const offMarketBus = useEventBus('cta_off_market_clicked');
const searchSuggestEmptyStateEventBus = useEventBus<string>(
	'search_suggest_empty_state'
);

const offMarketCleanup = offMarketBus.on(() => {
	trackCtaOffMarketClicked({
		[MixpanelProps.CTA_TEXT]: 'Imóveis Off-Market',
		[MixpanelProps.CURRENT_PAGE]: 'Home',
		[MixpanelProps.SECTION_NAME]: 'selected properties',
		[MixpanelProps.DESTINATION_URL]: '/off-market'
	});
});

const searchSuggestCleanup = searchSuggestEmptyStateEventBus.on(
	handleSearchSuggestEmptyState
);

function handleViewMoreClicked(
	sectionName: SectionName,
	payload: ViewMoreClickedPayload
) {
	trackCtaViewMoreClicked({
		[MixpanelProps.CTA_TEXT]: payload.ctaText,
		[MixpanelProps.CURRENT_PAGE]: 'Home',
		[MixpanelProps.SECTION_NAME]: sectionName,
		[MixpanelProps.DESTINATION_URL]: payload.href,
		[MixpanelProps.CLICK_COUNT]: payload.clickCount
	});
}

function handleCarouselNeighborhoodClicked({
	clickLocation,
	cardText,
	neighborhoodName,
	destinationUrl
}: SelectedRegionsSectionClick) {
	trackCarouselNeighborhoodClicked({
		[MixpanelProps.CARD_TEXT]: cardText ?? '',
		[MixpanelProps.NEIGHBORHOOD_NAME]: neighborhoodName,
		[MixpanelProps.DESTINATION_URL]: destinationUrl,
		[MixpanelProps.CLICK_NEIGHBORHOOD_TAB]:
			clickLocation === 'neighborhood tab',
		[MixpanelProps.CLICK_NEIGHBORHOOD_CARD]:
			clickLocation === 'neighborhood card',
		[MixpanelProps.CLICK_NEIGHBORHOOD_TITLE]:
			clickLocation === 'neighborhood title',
		[MixpanelProps.CLICK_CARD]: clickLocation === 'card clicked'
	});
}

function handleSearchSuggestEmptyState(searchTerm: string) {
	trackEmptyStateFilter({
		[MixpanelProps.TEXT_SEARCH]: searchTerm
	});
}

function handleFilterApplied(event: FilterApplied) {
	trackFilterApplied(event);
}

function handleQuickNeighborhoodFilterApplied(
	event: QuickNeighborhoodFilterApplied
) {
	trackQuickNeighborhoodFilterApplied(event);
}

function handleCardHeroOpened(event: CardHeroOpened) {
	trackCardHeroOpened(event);
}

onUnmounted(() => {
	offMarketCleanup();
	searchSuggestCleanup();
});
</script>

<template>
	<div>
		<div class="relative">
			<!-- Hero is NOT lazy-loaded because it contains the LCP image -->
			<SectionsHeroSectionHero @filter-applied="handleFilterApplied" />
		</div>
		<!-- Selecionados para você: Client-only, não renderiza no SSR -->
		<ClientOnly>
			<SectionsRecommendedPropertiesSection
				v-if="shouldShowSelectedProperties"
				:properties="selectedProperties?.properties ?? []"
				:cta-link="ctaLink"
				:is-loading="isLoadingSelectedProperties"
				container-class="bg-primary"
				@cta-view-more-clicked="
					(payload: ViewMoreClickedPayload) =>
						handleViewMoreClicked('selected properties', payload)
				"
			/>
		</ClientOnly>

		<!-- ValueProposition is NOT lazy-hydrated per requirements -->
		<LazySectionsValueProposition v-if="ft_cwb_home_sliders" />

		<LazyVisibleSectionsPilarFeaturesSection
			v-if="ft_cwb_home_sliders"
			hydrate-on-visible
		/>

		<div class="flex flex-col bg-primary">
			<div role="separator" class="my-1 h-[0.0625rem] bg-primary-light-4"></div>
			<LazyVisibleSectionsRegionsSection hydrate-on-visible />
			<div role="separator" class="my-1 h-[0.0625rem] bg-primary-light-4"></div>
		</div>

		<LazySectionsExclusivePropertiesSection
			:properties="exclusiveProperties?.properties ?? []"
			:is-loading="isLoadingExclusiveProperties"
			:title-variant="ft_cwb_home_sliders ? 'default' : 'alternative'"
		/>

		<LazyVisibleSectionsCuratedListsSectionDeprecatedSection
			v-if="!ft_cwb_home_sliders"
			hydrate-on-visible
		/>

		<LazyVisibleSectionsChosenPropertiesSection
			v-if="!ft_cwb_home_sliders"
			hydrate-on-visible
			:properties="selectedProperties?.properties ?? []"
			:cta-link="ctaLink"
			:is-loading="isLoadingSelectedProperties"
			@cta_view_more_clicked="
				payload => handleViewMoreClicked('selected properties', payload)
			"
		/>

		<LazyVisibleSectionsSelectedRegionsSection
			hydrate-on-visible
			:regions="regions"
			:is-loading="false"
			@cta_view_more_clicked="
				payload => handleViewMoreClicked('selected properties', payload)
			"
			@carousel_neighborhood_clicked="handleCarouselNeighborhoodClicked"
		/>

		<LazyVisibleSectionsCuratedListsSection
			v-if="ft_cwb_home_sliders"
			hydrate-on-visible
		/>

		<LazyVisibleSectionsTestimonialsSection
			hydrate-on-visible
			:testimonials="testimonials"
			:variant="testimonialsSectionVariant"
		/>

		<LazyVisibleSectionsNeighborhoodLinkingSection hydrate-on-visible />
		<LazyVisibleSectionsPoiAddressLinkingSection hydrate-on-visible />
	</div>
</template>
